// Состав включённых прикладных модулей сервера — подсказка интерфейсу,
// а не авторизация (ADR-0083, `docs/spec/core/di.md`).
//
// Неизвестный состав не прячет раздел насовсем: пока набор грузится или
// загрузка упала, `isEnabled` отвечает `false`, а потребитель по
// `status` показывает ожидание или ошибку с повтором, а не «раздела нет»
// (ADR-0083, п. 4). Упавшее перечитывание после успеха статус не
// меняет: последний известный состав остаётся в силе.
//
// Скоуп кэша — user-scoped: состав от организации не зависит, а
// org-less сессия (`/select-org`) тоже должна его получить. Ключ с
// `userId` даёт свежий запрос после входа другого пользователя.

import { useMemo } from "react";

import { useUserScopedQuery } from "../../query/scope";
import { fetchEnabledModules } from "./api";
import type { EnabledModulesResponse } from "./api";
import { isModuleName } from "./names";
import type { ModuleName } from "./names";

// Состав меняется только перезапуском сервера, а устаревание лечится
// перезагрузкой страницы (ADR-0083, п. 5): перечитывать нечего.
const MODULES_STALE_TIME_MS = Number.POSITIVE_INFINITY;

export interface EnabledModulesState {
  readonly status: "loading" | "error" | "ready";
  /** Модуль в наборе. Пока набор не загружен или загрузка упала — `false` (ADR-0083, п. 4). */
  readonly isEnabled: (module: ModuleName) => boolean;
  readonly retry: () => void;
}

export function useEnabledModules(): EnabledModulesState {
  const query = useUserScopedQuery<EnabledModulesResponse>({
    key: ["modules"],
    queryFn: () => fetchEnabledModules(),
    retry: false,
    staleTime: MODULES_STALE_TIME_MS,
  });
  const names = query.data?.modules;
  const refetch = query.refetch;
  const isError = query.isError;
  // Мемо по ссылке на ответ: потребители кладут `isEnabled` в зависимости
  // эффектов и мемо, новый объект на каждом рендере их бы перезапускал.
  return useMemo(() => {
    const enabled: ReadonlySet<string> = new Set(
      (names ?? []).filter(isModuleName),
    );
    return {
      status: names !== undefined ? "ready" : isError ? "error" : "loading",
      isEnabled: (module: ModuleName) => enabled.has(module),
      retry: () => {
        void refetch();
      },
    };
  }, [names, isError, refetch]);
}
